// SCR-STAFF-517: یادآوری بالینی (US-CLN-154).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, ListPage, StatusChip, toJalali, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { clnKeys, SCR_STAFF_517 } from './common';

type ReminderStatus = 'Open' | 'Due' | 'Done' | 'Cancelled';

interface ClinicalReminderView {
  id: string;
  patientId: string;
  internalText: string;
  dueOn: string;
  suggestedServiceId: string | null;
  ownerMembershipId: string;
  notifyPatient: boolean;
  followUpBooking: boolean;
  status: ReminderStatus;
  createdAt: string;
}

export function ClinicalRemindersPage() {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [params] = useSearchParams();
  const patientId = params.get('patientId') ?? '';
  const canManage = access.accessMode === 'Full' && access.can('cln.reminder.manage');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ internalText: '', dueOn: '', dueInDays: '', notifyPatient: true, followUpBooking: true });

  const list = useQuery({
    queryKey: clnKeys.reminders(session?.tenantId, patientId),
    enabled: Boolean(patientId) && canManage,
    queryFn: async () =>
      (await unwrap(
        tenantApi.GET('/api/v1/clinical/patients/{patientId}/reminders', {
          params: { path: { patientId } },
        }),
      )) as ClinicalReminderView[],
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: clnKeys.reminders(session?.tenantId, patientId) });

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/patients/{patientId}/reminders', {
          params: { path: { patientId } },
          headers: { 'Idempotency-Key': key.current() },
          body: {
            internalText: form.internalText.trim(),
            dueOn: form.dueOn || null,
            dueInDays: form.dueInDays ? Number(form.dueInDays) : null,
            suggestedServiceId: null,
            ownerMembershipId: null,
            notifyPatient: form.notifyPatient,
            followUpBooking: form.followUpBooking,
          },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setCreating(false);
      setForm({ internalText: '', dueOn: '', dueInDays: '', notifyPatient: true, followUpBooking: true });
      refresh();
    },
  });

  const close = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'done' | 'cancel' }) => {
      const params = { path: { patientId, reminderId: id } };
      return action === 'done'
        ? unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/reminders/{reminderId}:done', { params }))
        : unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/reminders/{reminderId}:cancel', { params }));
    },
    onSuccess: refresh,
  });

  const columns: DataTableColumn<ClinicalReminderView>[] = [
    { id: 'text', headerKey: 'cln:reminders.internalText', cell: (row) => row.internalText },
    { id: 'due', headerKey: 'cln:reminders.dueOn', cell: (row) => toJalali(row.dueOn, { timeZone }) },
    {
      id: 'status',
      headerKey: 'cln:common.status',
      cell: (row) => (
        <StatusChip
          tone={row.status === 'Due' ? 'error' : row.status === 'Open' ? 'pending' : 'done'}
          labelKey={`cln:reminders.status.${row.status}`}
        />
      ),
    },
    {
      id: 'actions',
      headerKey: 'cln:common.actions',
      cell: (row) =>
        canManage && (row.status === 'Open' || row.status === 'Due') ? (
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => close.mutate({ id: row.id, action: 'done' })} data-testid="cln-reminder-done">
              {t('reminders.markDone')}
            </Button>
            <Button size="small" color="inherit" onClick={() => close.mutate({ id: row.id, action: 'cancel' })} data-testid="cln-reminder-cancel">
              {t('reminders.cancel')}
            </Button>
          </Stack>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_517}
      titleKey="cln:reminders.title"
      subtitleKey="cln:reminders.scopeMineTeam"
      actions={
        patientId && canManage ? (
          <Button variant="contained" onClick={() => setCreating(true)} data-testid="cln-reminder-create">
            {t('reminders.create')}
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={2} data-testid="clinical-reminders-page">
        <Alert severity="info">{t('reminders.neutralHint')}</Alert>
        {!patientId ? (
          <Typography variant="body2" color="text.secondary">
            {t('reminders.selectPatient')}
          </Typography>
        ) : (
          <>
            <Button component={RouterLink} to={`/patients/${patientId}?tab=clinical`} variant="outlined" size="small">
              {t('reminders.backToRecord')}
            </Button>
            <DataTable
              captionKey="cln:reminders.title"
              columns={columns}
              rows={list.data}
              getRowId={(row) => row.id}
              isLoading={list.isPending}
              error={list.error}
              onRetry={() => void list.refetch()}
              emptyTitleKey="cln:reminders.empty"
            />
          </>
        )}
      </Stack>
      <FormDialog
        open={creating}
        titleKey="cln:reminders.create"
        onSubmit={() => create.mutate()}
        onClose={() => setCreating(false)}
        isDirty={Boolean(form.internalText || form.dueOn || form.dueInDays)}
        isSubmitting={create.isPending}
        submitError={create.error}
      >
        <TextField
          label={t('reminders.internalText')}
          required
          multiline
          minRows={2}
          value={form.internalText}
          onChange={(event) => setForm((current) => ({ ...current, internalText: event.target.value }))}
          slotProps={{ htmlInput: { 'data-testid': 'cln-reminder-text' } }}
        />
        <TextField
          label={t('reminders.dueOn')}
          type="date"
          value={form.dueOn}
          onChange={(event) => setForm((current) => ({ ...current, dueOn: event.target.value }))}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { dir: 'ltr' } }}
        />
        <TextField
          label={t('reminders.dueInDays')}
          value={form.dueInDays}
          onChange={(event) => setForm((current) => ({ ...current, dueInDays: event.target.value }))}
          slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr' } }}
        />
        <FormControlLabel
          control={<Checkbox checked={form.notifyPatient} onChange={(event) => setForm((current) => ({ ...current, notifyPatient: event.target.checked }))} />}
          label={t('reminders.notifyPatient')}
        />
        <FormControlLabel
          control={<Checkbox checked={form.followUpBooking} onChange={(event) => setForm((current) => ({ ...current, followUpBooking: event.target.checked }))} />}
          label={t('reminders.followUpBooking')}
        />
      </FormDialog>
    </ListPage>
  );
}
